import React, { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { publishEvent } from '@zet/analytics'
import { MotiView } from '@zet/common/components/motiView'
import { useToast } from '@zet/common/components/toast'
import { AgentDetails } from '@zet/customer'
import {
  B4,
  B5,
  Button,
  colors,
  Icon,
  Input,
  Pressable,
  SH2,
  SH3,
  Stack,
  XStack,
} from '@zet/design-system'
import { i18n } from '@zet/localization'
import { MOBILE_NUMBER_REGEX } from '@zet/utils/regularExpression'
import { useUnsafeVariable } from 'nativewind'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import PhoneRoundOutlined from '../../../../../customer/assets/svgs/phoneRoundOutlined.svg'
import { useGetNextStateForProductFlow } from '../../../../api/brands/hooks'
import { BottomsheetTypes } from '../../../../api/brands/types'
import { useProductFlowApply } from '../../../../hooks/useProductFlowApply'
import { Header } from '../header'

const SecondaryPhoneNumbers = ({
  agentData,
  handleBottomsheetState,
  data,
}: {
  agentData: AgentDetails
  handleBottomsheetState: (state: BottomsheetTypes, props: any) => void
  data?: any
}) => {
  const [showAll, setShowAll] = useState(false)
  const [colorTertiary200] = useUnsafeVariable('--color-tertiary-200')
  const [colorGrays50] = useUnsafeVariable('--color-helpAndSupport-gray150')
  const { mutate, isLoading } = useGetNextStateForProductFlow()
  const toast = useToast()

  function closeBottomSheet() {
    handleBottomsheetState(null, null)
  }

  const { apply, shareLinkLoading } = useProductFlowApply(
    agentData,
    handleBottomsheetState
  )

  const maskedMobileNumbers = data?.maskedMobileNumbers ?? []

  type ValidationSchema = z.infer<typeof validationSchema>

  const validationSchema = z.object({
    secondaryPhoneNumber: z
      .string()
      .min(1, { message: 'Phone number is required' })
      .regex(
        new RegExp(MOBILE_NUMBER_REGEX),
        'The entered number does not match the required format'
      )
      .max(10, { message: 'Phone number cannot exceed 10 digits' })
      .refine(
        (value) => {
          // Extract first two digits and last three digits of the input
          const firstTwoDigits = value.slice(0, 2)
          const lastThreeDigits = value.slice(-3)

          // Check if any number in the array matches the first two and last three digits
          return maskedMobileNumbers.some(
            (number: string) =>
              number.startsWith(firstTwoDigits) &&
              number.endsWith(lastThreeDigits)
          )
        },
        {
          message: 'The entered number does not match the list',
        }
      ),
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    getValues,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const numbersToDisplay = showAll
    ? maskedMobileNumbers
    : maskedMobileNumbers.slice(0, 3)

  const onSubmit = () => {
    const { secondaryPhoneNumber } = getValues()
    publishEvent('click_action', {
      item_name: 'shortflow_secondary_phone_number',
    })
    mutate(
      {
        customerPayload: {
          secondaryPhoneNumber,
          customerId: sessionStorage.getItem('customerId'),
        },
        userState: 'SHOWN_SECONDARY_PHONE_NUMBER_SCREEN_V3',
      },
      {
        onSuccess(data) {
          sessionStorage.setItem('secondaryPhoneNumber', secondaryPhoneNumber)
          if (data?.action === 'SHOW_SECONDARY_OTP_SCREEN_V3') {
            handleBottomsheetState('OTP_SECONDARY_PHONE_VERIFICATION', {
              secondaryPhoneNumber,
            })
          }
          if (data?.action === 'SHOW_RECOMMENDATION_SCREEN') {
            apply()
          }
          if (data?.lockedTill) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description: data?.reason,
              hideAfter: 3000,
            })
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      }
    )
  }

  return (
    <>
      <Stack>
        {/* Header */}
        <Header
          closeBottomSheet={closeBottomSheet}
          subTitle={i18n.t(
            'webFlow.verifySecondaryPhoneNumber.foundMultuplePhoneNumber'
          )}
          title={i18n.t('webFlow.verifySecondaryPhoneNumber.verifyPhoneNumber')}
        />
        {/* Numbers linked */}
        <Stack className="my-3 px-3">
          <Stack className="border-grays-40 rounded-lg border-[1px] p-3">
            <SH3 className="text-grays-80 mb-2">
              {i18n.t('webFlow.verifySecondaryPhoneNumber.numbersLinked')}
            </SH3>
            <Stack className="mt-2">
              {numbersToDisplay.map((number: string, index: number) => {
                return (
                  <MotiView
                    key={index}
                    from={{ opacity: !showAll ? 1 : 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ type: 'timing', duration: 300 }}
                  >
                    <XStack className="bg-grays-10 mb-1 items-center p-2">
                      <PhoneRoundOutlined width={18} height={18} />
                      <SH2 className="pl-2 text-[#141216]">{number}</SH2>
                    </XStack>
                  </MotiView>
                )
              })}
            </Stack>
            <XStack className="mt-2 items-center justify-between">
              <B5 className="text-grays-60 mb-2  mt-1 ">
                {i18n.t('verifySecondaryPhoneNumber.total')}{' '}
                {maskedMobileNumbers?.length}{' '}
                {i18n.t('verifySecondaryPhoneNumber.numbersFound')}
              </B5>
              {maskedMobileNumbers?.length > 3 && !showAll ? (
                <Pressable
                  hitSlop={{ top: 10, bottom: 10, left: 10 }}
                  onPress={() => setShowAll(!showAll)}
                  className="flex-row items-center"
                >
                  <B4 className="text-primary-100 text-xs">
                    {i18n.t('verifySecondaryPhoneNumber.seeMore')}
                  </B4>
                  <Icon
                    type={showAll ? 'ArrowTop' : 'ArrowDown'}
                    size={22}
                    color={colors.primary[100]}
                    className="ml-2"
                  />
                </Pressable>
              ) : null}
            </XStack>
          </Stack>
          <Stack className="my-4">
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Eg: 9910687765"
                  onBlur={onBlur}
                  onChangeText={(value) => {
                    onChange(value)
                  }}
                  keyboardType="numeric"
                  label={i18n.t('verifySecondaryPhoneNumber.enterLinkedNumber')}
                  isRequired
                  value={value}
                  type={
                    (getValues()?.secondaryPhoneNumber?.trim()?.length === 0 &&
                      !touchedFields.secondaryPhoneNumber) ||
                    getValues()?.secondaryPhoneNumber === undefined
                      ? 'primary'
                      : !isValid
                      ? 'error'
                      : 'success'
                  }
                  maxLength={10}
                  iconRight={
                    getValues()?.secondaryPhoneNumber?.length === 0 ? (
                      <Icon
                        color={colors.error[100]}
                        type="ErrorOutlined"
                        size={28}
                        className="mr-2"
                      />
                    ) : typeof getValues()?.secondaryPhoneNumber ===
                      'undefined' ? null : !isValid ? (
                      <Icon
                        color={colors.error[100]}
                        type="ErrorOutlined"
                        size={28}
                        className="mr-2"
                      />
                    ) : (
                      <Icon
                        color={colors.success[100]}
                        type="DoneOutlined"
                        size={28}
                        className="mr-2"
                      />
                    )
                  }
                />
              )}
              name="secondaryPhoneNumber"
            />
            {errors.secondaryPhoneNumber?.message ? (
              <Stack className="-mt-3">
                <B5 className="text-error-100">
                  {errors.secondaryPhoneNumber?.message}
                </B5>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
        <Stack className="border-t-grays-30 border-t px-3 pt-3">
          <Button
            size={'lg'}
            className={'w-full'}
            style={{
              backgroundColor: isValid ? colorTertiary200 : colorGrays50,
            }}
            textStyle={'text-base'}
            disabled={!isValid}
            onPress={handleSubmit(onSubmit)}
            loading={isLoading || shareLinkLoading}
          >
            {i18n.t('verifySecondaryPhoneNumber.sendOtp')}
          </Button>
          <Button
            size={'lg'}
            outlined={true}
            className={
              'border-tertiary-200 my-2 w-full  bg-white hover:border-black hover:bg-[rgba(0,0,0,0.1)] focus:bg-[rgba(0,0,0,0.1)] active:border-black active:bg-[rgba(0,0,0,0.1)]'
            }
            opacity={false}
            onPress={() =>
              handleBottomsheetState('DONT_HAVE_ACCESS_TO_NUMBERS', data)
            }
            textStyle={'text-tertiary-200 text-base'}
          >
            {i18n.t('verifySecondaryPhoneNumber.dontHaveAccesstoNumbers')}{' '}
          </Button>
        </Stack>
      </Stack>
    </>
  )
}

export default SecondaryPhoneNumbers
